import { useMemo } from "react";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import { getUpdateOrCreateVariables } from "components/Shared/Forms/AddressFieldset";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import { assertPresence } from "utils/assertions";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import {
  processApolloError,
  ManageManufacturingFacilityFormFields,
} from "./Form";
import ManufacturingFacilityFormSection from "./FormSection";

const fragment = gql(/* GraphQL */ `
  fragment ProductLinePanelEditManufacturingFacility on ManufacturingFacility {
    id
    name
    address {
      id
      line1
      line2
      city
      state
      zip
    }
  }
`);

const updateMutation = gql(/* GraphQL */ `
  mutation ProductLinePanelUpdateManufacturingFacility(
    $facilityId: UUID!
    $facilityData: ManufacturingFacilityPatch!
    $isAddressUpdatedOrCreated: Boolean = false
    $addressId: UUID
    $addressData: AddressPatch
  ) {
    ...UpdateOrCreateAddress
    updateManufacturingFacility(
      input: { id: $facilityId, patch: $facilityData }
    ) {
      manufacturingFacility {
        id
        name
        ...ProductLinePanelManufacturingFacilityListItem
        ...ProductLinePanelEditManufacturingFacility
      }
    }
  }
`);

type Props = {
  facility: FragmentType<typeof fragment> | null | undefined;
  formId: string;
  isLoading: boolean;
  onCancel: () => void;
  onUpdated: () => void;
};

export default function EditManufacturingFacilityForm({
  facility,
  formId,
  isLoading,
  onCancel,
  onUpdated,
}: Props) {
  const [updateFacility] = useBrandMutation(updateMutation);

  const facilityData = getFragmentData(fragment, facility);

  const defaultValues = useMemo<
    ManageManufacturingFacilityFormFields | undefined
  >(() => {
    if (!facilityData) return;
    return {
      name: facilityData.name,
      address: {
        line1: facilityData.address?.line1,
        line2: facilityData.address?.line2,
        city: facilityData.address?.city,
        state: facilityData.address?.state,
        zip: facilityData.address?.zip,
      },
    };
  }, [facilityData]);

  const [handleUpdate, { isLoading: isUpdating }] = useCreateValidSubmitHandler(
    async ({
      address,
      ...patchData
    }: ManageManufacturingFacilityFormFields) => {
      assertPresence(facilityData);

      const addressVariables = getUpdateOrCreateVariables(
        facilityData.address?.id,
        address,
      );

      assertPresence(addressVariables.addressId);

      await updateFacility({
        variables: {
          facilityId: facilityData.id,
          facilityData: {
            ...patchData,
            addressId: addressVariables.addressId,
          },
          ...addressVariables,
        },
      });
    },
    {
      onUpdateComplete: () => {
        onUpdated();
      },
      processApolloError,
      successMessage: "Manufacturing facility updated successfully.",
    },
  );

  return (
    <ManufacturingFacilityFormSection
      defaultValues={defaultValues}
      formId={formId}
      isLoading={isLoading}
      isSaving={isUpdating}
      modeLabel="Edit"
      onNavigateToList={onCancel}
      onCancel={onCancel}
      onValidSubmit={handleUpdate}
    />
  );
}
